import React, { useState } from "react";
import { Btn } from "../components";
import { T } from "../theme";

type Props = {
  step: number;
  total: number;
  isNativeIOS: boolean;
  isMobile: boolean;
  onApple: () => void;
  onGoogle: () => void;
  onMagic: (email: string) => void;
  onSkip: () => void;
  onBack: () => void;
};

const EMAIL_RE = /^\S+@\S+\.\S+$/;

const AppleLogo = (): React.ReactElement => (
  <svg width="18" height="22" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
  </svg>
);

const GoogleLogo = (): React.ReactElement => (
  <svg width="18" height="18" viewBox="0 0 48 48" fill="none" aria-hidden="true">
    <path d="M44 24c0-1.4-.1-2.7-.4-4H24v8h11.3c-.5 2.6-2 4.8-4.2 6.3v5.2h6.8C41.8 35.9 44 30.4 44 24z" fill="#4285F4" />
    <path d="M24 44c5.7 0 10.4-1.9 13.9-5.1l-6.8-5.2c-1.9 1.3-4.3 2-7.1 2-5.5 0-10.1-3.7-11.7-8.7H5.2v5.3C8.7 39.4 15.8 44 24 44z" fill="#34A853" />
    <path d="M12.3 27.1c-.4-1.2-.6-2.5-.6-3.8s.2-2.6.6-3.8v-5.3H5.2C3.8 17.1 3 20.4 3 24s.8 6.9 2.2 9.9l7.1-6.8z" fill="#FBBC04" />
    <path d="M24 11.5c3.1 0 5.8 1.1 8 3.1l6-6C34.4 5.1 29.7 3 24 3 15.8 3 8.7 7.6 5.2 14.2l7.1 5.3c1.6-5 6.2-8 11.7-8z" fill="#EA4335" />
  </svg>
);

const MailIcon = (): React.ReactElement => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 7l9 6 9-6" />
  </svg>
);

export function AccountScreen({
  step,
  total,
  isNativeIOS,
  isMobile,
  onApple,
  onGoogle,
  onMagic,
  onSkip,
  onBack,
}: Props): React.ReactElement {
  const [email, setEmail] = useState("");
  const [emailOpen, setEmailOpen] = useState(false);
  const [submitting, setSubmitting] = useState<null | "apple" | "google" | "magic">(null);
  const [magicSent, setMagicSent] = useState(false);
  const validEmail = EMAIL_RE.test(email);

  // While auth is in flight (OAuth redirect or magic-link request), we render
  // a quiet interstitial in place of the buttons so the screen doesn't look
  // dead while the system sheet / page navigation kicks in.
  if (submitting === "apple" || submitting === "google") {
    const label = submitting === "apple" ? "Continuing with Apple…" : "Continuing with Google…";
    return (
      <Interstitial label={label} isMobile={isMobile} step={step} total={total} onBack={() => setSubmitting(null)} />
    );
  }

  if (magicSent) {
    return (
      <CheckInbox
        email={email}
        step={step}
        total={total}
        isMobile={isMobile}
        isNativeIOS={isNativeIOS}
        onBack={() => setMagicSent(false)}
        onSkip={onSkip}
      />
    );
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: T.bgPage,
        color: T.fg,
        display: "flex",
        flexDirection: "column",
        zIndex: 250,
        fontFamily: "'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Arial",
      }}
    >
      <div
        style={{
          padding: isMobile ? "max(14px, env(safe-area-inset-top)) 20px 8px" : "18px 28px 8px",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <button
          type="button"
          onClick={onBack}
          aria-label="Back"
          style={{
            background: "transparent",
            border: "none",
            color: T.fg,
            padding: 4,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <div style={{ flex: 1, minHeight: 4, display: "flex", gap: 6 }}>
          {Array.from({ length: total }).map((_, i) => (
            <div
              key={i}
              style={{ flex: 1, height: 3, borderRadius: 2, background: i < step ? T.fg : "rgba(255,255,255,0.18)" }}
            />
          ))}
        </div>
        <span style={{ width: 22 }} />
      </div>

      <div
        style={{
          flex: 1,
          padding: isMobile ? "16px 20px 24px" : "28px 28px 24px",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <h1
          style={{
            fontSize: isMobile ? "1.85rem" : "2.2rem",
            fontWeight: 700,
            letterSpacing: "-0.025em",
            lineHeight: 1.1,
            margin: 0,
          }}
        >
          {isNativeIOS ? "Don't lose your plan." : "Save your plan."}
        </h1>
        <p style={{ color: T.fg2, fontSize: "0.96rem", lineHeight: 1.45, margin: "8px 0 0" }}>
          {isNativeIOS
            ? "Sign in to lock it in and sync across your iPhone, iPad, and the web. One tap and you're done."
            : "So it follows you across devices. Your plan is already saved on this device — sign-in is optional."}
        </p>

        <div style={{ marginTop: 28, display: "grid", gap: 10 }}>
          {/* Apple — visible on web AND iOS. Supabase OAuth handles both surfaces. */}
          <Btn
            kind="white"
            size="lg"
            full
            onClick={async () => {
              setSubmitting("apple");
              try {
                await onApple();
              } finally {
                setSubmitting(null);
              }
            }}
            icon={<AppleLogo />}
          >
            Continue with Apple
          </Btn>
          <Btn
            kind="secondary"
            size="lg"
            full
            onClick={async () => {
              setSubmitting("google");
              try {
                await onGoogle();
              } finally {
                setSubmitting(null);
              }
            }}
            icon={<GoogleLogo />}
          >
            Continue with Google
          </Btn>

          {emailOpen ? (
            <div
              style={{
                padding: "14px 14px",
                borderRadius: 12,
                background: "rgba(255,255,255,0.04)",
                border: `1px solid ${T.hairlineStrong}`,
              }}
            >
              <label style={{ display: "block", color: T.fg2, fontSize: "0.78rem", fontWeight: 600, marginBottom: 8 }}>
                Email address
              </label>
              <input
                type="email"
                autoComplete="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                style={{
                  width: "100%",
                  height: 48,
                  padding: "0 14px",
                  borderRadius: 10,
                  background: "rgba(255,255,255,0.04)",
                  border: `1px solid ${T.hairlineStrong}`,
                  color: T.fg,
                  fontSize: "1rem",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
                <Btn
                  kind="primary"
                  size="md"
                  full
                  disabled={!validEmail || submitting === "magic"}
                  onClick={async () => {
                    setSubmitting("magic");
                    try {
                      await onMagic(email.trim());
                      setMagicSent(true);
                    } finally {
                      setSubmitting(null);
                    }
                  }}
                >
                  Email me a sign-in link
                </Btn>
              </div>
            </div>
          ) : (
            <Btn kind="secondary" size="lg" full onClick={() => setEmailOpen(true)} icon={<MailIcon />}>
              Continue with email
            </Btn>
          )}
        </div>

        {isNativeIOS ? null : (
          <div style={{ marginTop: 22, display: "flex", justifyContent: "center" }}>
            <button
              type="button"
              onClick={onSkip}
              style={{
                background: "transparent",
                border: "none",
                color: T.muted,
                fontSize: "0.86rem",
                fontWeight: 600,
                cursor: "pointer",
                padding: "10px 16px",
                textDecoration: "underline",
                textUnderlineOffset: 3,
                textDecorationColor: "rgba(255,255,255,0.22)",
              }}
            >
              Continue without signing in
            </button>
          </div>
        )}

        <div style={{ marginTop: "auto", paddingTop: 24, color: T.muted, fontSize: "0.72rem", textAlign: "center", lineHeight: 1.5 }}>
          By continuing you agree to the Terms and Privacy Policy.
        </div>
      </div>
    </div>
  );
}

function Interstitial({
  label,
  isMobile,
  step,
  total,
  onBack,
}: {
  label: string;
  isMobile: boolean;
  step: number;
  total: number;
  onBack: () => void;
}): React.ReactElement {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: T.bgPage,
        color: T.fg,
        display: "flex",
        flexDirection: "column",
        zIndex: 250,
        fontFamily: "'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Arial",
      }}
    >
      <div
        style={{
          padding: isMobile ? "max(14px, env(safe-area-inset-top)) 20px 8px" : "18px 28px 8px",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <span style={{ width: 22 }} />
        <div style={{ flex: 1, minHeight: 4, display: "flex", gap: 6 }}>
          {Array.from({ length: total }).map((_, i) => (
            <div
              key={i}
              style={{ flex: 1, height: 3, borderRadius: 2, background: i < step ? T.fg : "rgba(255,255,255,0.18)" }}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={onBack}
          style={{
            background: "transparent",
            border: "none",
            color: T.fg2,
            padding: "0 8px",
            cursor: "pointer",
            fontSize: "0.86rem",
            fontWeight: 600,
          }}
        >
          Cancel
        </button>
      </div>
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 24,
          padding: "0 28px",
          textAlign: "center",
        }}
      >
        <div
          aria-hidden
          style={{
            width: 28,
            height: 28,
            borderRadius: 999,
            border: "2px solid rgba(255,255,255,0.18)",
            borderTopColor: T.fg,
            animation: "wp-onboarding-spin 0.7s linear infinite",
          }}
        />
        <div style={{ fontSize: "1.1rem", fontWeight: 700, letterSpacing: "-0.01em" }}>{label}</div>
        <div style={{ color: T.fg2, fontSize: "0.84rem", maxWidth: 280, lineHeight: 1.5 }}>
          We'll bring you back to your plan once you're signed in.
        </div>
        <style>{`@keyframes wp-onboarding-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}

function CheckInbox({
  email,
  step,
  total,
  isMobile,
  isNativeIOS,
  onBack,
  onSkip,
}: {
  email: string;
  step: number;
  total: number;
  isMobile: boolean;
  isNativeIOS: boolean;
  onBack: () => void;
  onSkip: () => void;
}): React.ReactElement {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: T.bgPage,
        color: T.fg,
        display: "flex",
        flexDirection: "column",
        zIndex: 250,
        fontFamily: "'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Arial",
      }}
    >
      <div
        style={{
          padding: isMobile ? "max(14px, env(safe-area-inset-top)) 20px 8px" : "18px 28px 8px",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <button
          type="button"
          onClick={onBack}
          aria-label="Back"
          style={{
            background: "transparent",
            border: "none",
            color: T.fg,
            padding: 4,
            cursor: "pointer",
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <div style={{ flex: 1, minHeight: 4, display: "flex", gap: 6 }}>
          {Array.from({ length: total }).map((_, i) => (
            <div
              key={i}
              style={{ flex: 1, height: 3, borderRadius: 2, background: i < step ? T.fg : "rgba(255,255,255,0.18)" }}
            />
          ))}
        </div>
        <span style={{ width: 22 }} />
      </div>
      <div
        style={{
          flex: 1,
          padding: isMobile ? "16px 20px 24px" : "28px 28px 24px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{ width: 48, height: 48, borderRadius: 14, background: "rgba(132,204,22,0.12)", border: "1px solid rgba(132,204,22,0.32)", display: "grid", placeItems: "center", color: T.lime }}>
          <MailIcon />
        </div>
        <h1 style={{ fontSize: "1.85rem", fontWeight: 700, letterSpacing: "-0.025em", lineHeight: 1.1, margin: "20px 0 8px" }}>
          Check your inbox.
        </h1>
        <p style={{ color: T.fg2, fontSize: "0.96rem", lineHeight: 1.5, margin: 0 }}>
          We sent a one-tap sign-in link to <span style={{ color: T.fg, fontWeight: 700 }}>{email}</span>. Open it on this
          device.
        </p>
        {isNativeIOS ? null : (
          <div style={{ marginTop: "auto", display: "grid", gap: 8 }}>
            <Btn kind="secondary" size="lg" full onClick={onSkip}>
              Continue without signing in
            </Btn>
          </div>
        )}
      </div>
    </div>
  );
}
